import React, { useEffect, useMemo, useRef } from 'react';
import { formatDateTime } from 'utils/datetime';

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Builds a regex that matches "@name" for any of the given names, provided the
// mention isn't glued onto more text (it must be followed by whitespace or the
// end of the string). Names are matched longest-first so "Claude 12" isn't
// shadowed by a shorter "Claude 1".
const buildMentionRegex = (names) => {
  if (names.length === 0) return null;
  const alternation = [...names]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|');
  return new RegExp(`@(${alternation})(?=\\s|$)`, 'g');
};

// Renders a message body as plain text, except for "@name" substrings that
// match an actual member/agent of the channel, which are wrapped for a
// Slack-like mention highlight.
const renderBody = (body, mentionRegex) => {
  if (!mentionRegex) return body;
  return body
    .split(mentionRegex)
    .map((part, index) =>
      index % 2 === 1 ? (
        <span key={index} className="gh-chat-mention">@{part}</span>
      ) : (
        part
      )
    );
};

const MessageList = ({ messages, currentUserId, members = [], agents = [] }) => {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const mentionRegex = useMemo(
    () => buildMentionRegex([...members.map((member) => member.displayName), ...agents.map((agent) => agent.name)]),
    [members, agents]
  );

  if (!messages.length) {
    return (
      <div className="gh-box-body text-center text-muted-gh">
        <i className="bi bi-chat-dots fs-3 d-block mb-2" aria-hidden="true"></i>
        No messages yet
      </div>
    );
  }

  const agentIds = new Set(agents.map((agent) => agent.id));

  return (
    <div className="gh-chat-messages">
      {messages.map((message) => {
        const isOwn = message.userId === currentUserId;
        const isBot = agentIds.has(message.userId);
        return (
          <div
            key={message.id}
            className={`gh-chat-message${isOwn ? ' gh-chat-message-own' : ''}${isBot ? ' gh-chat-message-bot' : ''}`}
          >
            <div className="gh-chat-message-meta">
              <span className="fw-semibold">{message.username}</span>
              {isBot && (
                <i className="bi bi-stars gh-chat-message-bot-badge" aria-hidden="true" title={message.username}></i>
              )}
              <span className="text-muted-gh small">{formatDateTime(message.createdAt)}</span>
            </div>
            <div className="gh-chat-message-body">{renderBody(message.body, mentionRegex)}</div>
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
};

export default MessageList;
